export const UTM_ATTRIBUTION_COOKIE_NAME = 'utm_attribution'
export const UTM_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60
export const UTM_INFLOW_SESSION_STORAGE_KEY = 'utm_inflow_pushed'

export type UtmAttribution = {
  utm_source: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
}

type QueryValue = string | string[] | undefined

export const getQueryParamAsString = (value: QueryValue): string | undefined => {
  if (typeof value === 'string' && value.length > 0) return value
  if (Array.isArray(value) && typeof value[0] === 'string' && value[0].length > 0) {
    return value[0]
  }
  return undefined
}

export const parseUtmFromQuery = (query: Record<string, QueryValue>): UtmAttribution | null => {
  const utm_source = getQueryParamAsString(query.utm_source)
  if (!utm_source) return null

  const utm_medium = getQueryParamAsString(query.utm_medium)
  const utm_campaign = getQueryParamAsString(query.utm_campaign)
  const utm_content = getQueryParamAsString(query.utm_content)

  return {
    utm_source,
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_content && { utm_content }),
  }
}

export const serializeUtmAttribution = (utm: UtmAttribution): string => {
  return encodeURIComponent(JSON.stringify(utm))
}

export const parseUtmAttributionCookie = (
  cookieValue: string | undefined
): UtmAttribution | null => {
  if (!cookieValue) return null

  try {
    let raw = cookieValue
    try {
      raw = decodeURIComponent(cookieValue)
    } catch {
      // Cookie may already be decoded by the browser.
    }

    const parsed = JSON.parse(raw) as Partial<UtmAttribution>
    if (!parsed || typeof parsed.utm_source !== 'string' || !parsed.utm_source) {
      return null
    }

    return {
      utm_source: parsed.utm_source,
      ...(typeof parsed.utm_medium === 'string' &&
        parsed.utm_medium && { utm_medium: parsed.utm_medium }),
      ...(typeof parsed.utm_campaign === 'string' &&
        parsed.utm_campaign && { utm_campaign: parsed.utm_campaign }),
      ...(typeof parsed.utm_content === 'string' &&
        parsed.utm_content && { utm_content: parsed.utm_content }),
    }
  } catch {
    return null
  }
}

export const getUtmInflowKey = (utm: UtmAttribution): string => {
  return [utm.utm_source, utm.utm_medium ?? '', utm.utm_campaign ?? '', utm.utm_content ?? ''].join(
    '|'
  )
}

export const toUtmDataLayerFields = (
  utm: UtmAttribution | null
): Partial<Record<'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content', string>> => {
  if (!utm) return {}

  return {
    ...(utm.utm_source && { utm_source: utm.utm_source }),
    ...(utm.utm_medium && { utm_medium: utm.utm_medium }),
    ...(utm.utm_campaign && { utm_campaign: utm.utm_campaign }),
    ...(utm.utm_content && { utm_content: utm.utm_content }),
  }
}

/** undefined = not hydrated yet; null = known empty; object = cached attribution */
let utmAttributionCache: UtmAttribution | null | undefined

export const setUtmAttributionCache = (utm: UtmAttribution | null): void => {
  utmAttributionCache = utm
}

/** undefined = cache not hydrated yet */
export const getUtmAttributionCache = (): UtmAttribution | null | undefined => utmAttributionCache

/**
 * Prefer in-memory cache (set when UTM is written) to avoid re-parsing the cookie
 * on every GTM event. Falls back to the cookie value on first read.
 */
export const resolveUtmAttribution = (cookieValue?: string): UtmAttribution | null => {
  if (utmAttributionCache !== undefined) {
    return utmAttributionCache
  }

  const parsed = parseUtmAttributionCookie(cookieValue)
  utmAttributionCache = parsed
  return parsed
}

/** Test-only: reset module cache between cases. */
export const resetUtmAttributionCache = (): void => {
  utmAttributionCache = undefined
}
